/**
 * StoryBoardContext — global state for the Visual Story Board feature.
 *
 * One board per manuscript, persisted to localStorage (via useLocalStore)
 * for the same reason MyWritingContext persists chapters: losing a writer's
 * planning layout is a failure mode they can't shrug off. Keyed by storyId
 * so every manuscript gets its own independent board.
 *
 * The board stores planning metadata and relationships only — chapter
 * *content* always lives in MyWritingContext and is read through, never
 * copied here.
 */

import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useLocalStore } from '@/lib/useLocalStore';
import { createDefaultBoard, createDefaultNode, createDefaultSection, makeId } from '../utils/boardHelpers';

const StoryBoardContext = createContext(undefined);

export function StoryBoardProvider({ children }) {
  const [boards, setBoards] = useLocalStore('sr-story-boards', {});

  // A story with no saved board yet needs *some* board to read — but a
  // fresh createDefaultBoard() on every call would mint new array/id
  // references each render, defeating consumers' useMemo. Cache one default
  // per storyId until a real mutation persists an actual board for it.
  const defaultBoards = useRef(new Map());
  const getBoard = useCallback((storyId) => {
    if (boards[storyId]) return boards[storyId];
    if (!defaultBoards.current.has(storyId)) {
      defaultBoards.current.set(storyId, createDefaultBoard(storyId));
    }
    return defaultBoards.current.get(storyId);
  }, [boards]);

  const mutateBoard = useCallback((storyId, mutator) => {
    setBoards((prev) => {
      const current = prev[storyId] ?? createDefaultBoard(storyId);
      const next = mutator(current);
      return { ...prev, [storyId]: { ...next, updatedAt: Date.now() } };
    });
  }, [setBoards]);

  const addNode = useCallback((storyId, type, position) => {
    const node = createDefaultNode(type, position);
    mutateBoard(storyId, (board) => ({ ...board, nodes: [...board.nodes, node] }));
    return node;
  }, [mutateBoard]);

  const updateNode = useCallback((storyId, nodeId, updates) => {
    mutateBoard(storyId, (board) => ({
      ...board,
      nodes: board.nodes.map((n) => (n.id === nodeId ? { ...n, ...updates } : n)),
    }));
  }, [mutateBoard]);

  const setNodePosition = useCallback((storyId, nodeId, position) => {
    mutateBoard(storyId, (board) => ({
      ...board,
      nodes: board.nodes.map((n) => (n.id === nodeId ? { ...n, position } : n)),
    }));
  }, [mutateBoard]);

  const deleteNode = useCallback((storyId, nodeId) => {
    mutateBoard(storyId, (board) => ({
      ...board,
      nodes: board.nodes.filter((n) => n.id !== nodeId),
      connections: board.connections.filter((c) => c.sourceNodeId !== nodeId && c.targetNodeId !== nodeId),
    }));
  }, [mutateBoard]);

  const duplicateNode = useCallback((storyId, nodeId) => {
    mutateBoard(storyId, (board) => {
      const original = board.nodes.find((n) => n.id === nodeId);
      if (!original) return board;
      const copy = {
        ...original,
        id: makeId('node'),
        title: `${original.title} (Copy)`,
        position: { x: original.position.x + 40, y: original.position.y + 40 },
      };
      return { ...board, nodes: [...board.nodes, copy] };
    });
  }, [mutateBoard]);

  const addConnection = useCallback((storyId, sourceNodeId, targetNodeId, label = '') => {
    if (sourceNodeId === targetNodeId) return null;
    const connection = { id: makeId('conn'), sourceNodeId, targetNodeId, label };
    mutateBoard(storyId, (board) => {
      const exists = board.connections.some(
        (c) => c.sourceNodeId === sourceNodeId && c.targetNodeId === targetNodeId
      );
      if (exists) return board;
      return { ...board, connections: [...board.connections, connection] };
    });
    return connection;
  }, [mutateBoard]);

  const updateConnection = useCallback((storyId, connectionId, updates) => {
    mutateBoard(storyId, (board) => ({
      ...board,
      connections: board.connections.map((c) => (c.id === connectionId ? { ...c, ...updates } : c)),
    }));
  }, [mutateBoard]);

  const deleteConnection = useCallback((storyId, connectionId) => {
    mutateBoard(storyId, (board) => ({
      ...board,
      connections: board.connections.filter((c) => c.id !== connectionId),
    }));
  }, [mutateBoard]);

  const addSection = useCallback((storyId, position) => {
    const section = createDefaultSection(position);
    mutateBoard(storyId, (board) => ({ ...board, sections: [...board.sections, section] }));
    return section;
  }, [mutateBoard]);

  const updateSection = useCallback((storyId, sectionId, updates) => {
    mutateBoard(storyId, (board) => ({
      ...board,
      sections: board.sections.map((s) => (s.id === sectionId ? { ...s, ...updates } : s)),
    }));
  }, [mutateBoard]);

  const deleteSection = useCallback((storyId, sectionId) => {
    mutateBoard(storyId, (board) => ({
      ...board,
      sections: board.sections.filter((s) => s.id !== sectionId),
      nodes: board.nodes.map((n) => (n.sectionId === sectionId ? { ...n, sectionId: null } : n)),
    }));
  }, [mutateBoard]);

  const setNodeSection = useCallback((storyId, nodeId, sectionId) => {
    mutateBoard(storyId, (board) => ({
      ...board,
      nodes: board.nodes.map((n) => (n.id === nodeId ? { ...n, sectionId } : n)),
    }));
  }, [mutateBoard]);

  const value = useMemo(() => ({
    getBoard,
    addNode,
    updateNode,
    setNodePosition,
    deleteNode,
    duplicateNode,
    addConnection,
    updateConnection,
    deleteConnection,
    addSection,
    updateSection,
    deleteSection,
    setNodeSection,
  }), [
    getBoard, addNode, updateNode, setNodePosition, deleteNode, duplicateNode,
    addConnection, updateConnection, deleteConnection,
    addSection, updateSection, deleteSection, setNodeSection,
  ]);

  return <StoryBoardContext.Provider value={value}>{children}</StoryBoardContext.Provider>;
}

export function useStoryBoard() {
  const context = useContext(StoryBoardContext);
  if (!context) {
    throw new Error('useStoryBoard must be used within StoryBoardProvider');
  }
  return context;
}
